import { Directive, ElementRef, HostListener, OnInit, inject } from '@angular/core';
import { NgControl } from '@angular/forms';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Strips the "+1" country-code prefix and extracts up to 10 local digits.
 */
function extractCanadaDigits(raw: string): string {
  let str = raw;
  if (str.startsWith('+1')) {
    str = str.slice(2);
  } else if (str.startsWith('1') && str.length > 10) {
    str = str.slice(1);
  }
  return str.replace(/\D/g, '').slice(0, 10);
}

/**
 * Progressively formats 0–10 local digits as "+1 (NXX) NXX-XXXX".
 */
function formatCanadaPhone(digits: string): string {
  const d = digits.slice(0, 10);
  if (!d.length) return '';
  if (d.length <= 3) return `+1 (${d}`;
  if (d.length <= 6) return `+1 (${d.slice(0, 3)}) ${d.slice(3)}`;
  return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

const PREFIX = '+1 (';

function localDigitsBefore(value: string, cursorPos: number): number {
  const prefixLen = value.startsWith(PREFIX) ? PREFIX.length : 0;
  if (cursorPos <= prefixLen) return 0;
  return value.slice(prefixLen, cursorPos).replace(/\D/g, '').length;
}

function cursorAfterNthLocalDigit(formatted: string, n: number): number {
  const prefixLen = formatted.startsWith(PREFIX) ? PREFIX.length : 0;
  if (n === 0) return prefixLen;
  let count = 0;
  for (let i = prefixLen; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) {
      count++;
      if (count === n) return i + 1;
    }
  }
  return formatted.length;
}

// ─── Directive ───────────────────────────────────────────────────────────────

/**
 * Applies Canadian phone formatting (+1 (NXX) NXX-XXXX) to
 * Angular Material matInput fields with type="tel".
 *
 * Usage: add `type="tel"` to the `<input matInput>` element.
 */
@Directive({
  selector: 'input[matInput][type="tel"]',
  standalone: true,
})
export class CanadaPhoneMaskDirective implements OnInit {
  private readonly el = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private readonly ngControl = inject(NgControl, { optional: true, self: true });

  ngOnInit(): void {
    const initial = String(this.ngControl?.control?.value ?? '').trim();
    if (initial) {
      this.applyValue(formatCanadaPhone(extractCanadaDigits(initial)));
    }
  }

  @HostListener('input')
  onInput(): void {
    const input = this.el.nativeElement;
    const rawValue = input.value;
    const cursorPos = input.selectionStart ?? rawValue.length;

    const digitsBefore = localDigitsBefore(rawValue, cursorPos);
    const formatted = formatCanadaPhone(extractCanadaDigits(rawValue));

    if (formatted === rawValue) return;

    this.applyValue(formatted);

    requestAnimationFrame(() => {
      const newPos = cursorAfterNthLocalDigit(formatted, digitsBefore);
      input.setSelectionRange(newPos, newPos);
    });
  }

  private applyValue(formatted: string): void {
    const input = this.el.nativeElement;
    input.value = formatted;

    const ctrl = this.ngControl?.control;
    if (ctrl && ctrl.value !== formatted) {
      ctrl.setValue(formatted, { emitModelToViewChange: false });
      ctrl.markAsDirty();
    }
  }
}